// src/components/About.js
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Button,
  Container,
  Row,
  Col,
  Card,
  CardGroup,
  ListGroup,
} from "react-bootstrap";
import { FaRocket } from "react-icons/fa";
import { FaArrowRight } from "react-icons/fa";

function About() {
  const navigate = useNavigate();

  const handleGetStarted = () => {
    navigate("/collaborate");
  };

  const impactData = {
    treesCut: 17,
    co2Emissions: 35, // in kg
    waterUsage: 14000, // in liters
    energyUsed: 1300, // in kWh
  };

  return (
    <div>
      <Container fluid className="hero-section py-4">
        <Row className="align-items-center">
          {/* Left Section - Text and Buttons */}
          <Col xs={12} md={6} className="text-center text-md-start">
            <h3>Our Mission</h3>
            <p className="mb-3">
              E-RECEIPT is committed to helping you digitize your receipts to
              keep them safe and organized. No more lost receipts or paper
              clutter—just simple, secure, and accessible storage.
            </p>
            <h3>Our Vision</h3>
            <p className="mb-4">
              We envision a world where all receipts are digital, reducing paper
              waste and simplifying the way we manage our finances.
            </p>
            <div className="d-flex flex-column flex-md-row gap-3">
              <Button
                variant="success"
                size="lg"
                className="d-flex align-items-center justify-content-center"
                onClick={handleGetStarted}
              >
                <FaRocket size={25} />
                <span className="ms-3">Collaborate with us</span>
              </Button>
            </div>
          </Col>
          {/* Right Section - Image */}
          <Col xs={12} md={6} className="text-center">
            <video className="video-responsive" autoPlay muted loop playsInline>
              <source src="PaperMaking.mp4" type="video/mp4" />
              Your browser does not support the video tag.
            </video>
          </Col>
        </Row>
      </Container>
      <div>
        <Container className="mt-5">
          <h1 className="text-center mb-4">
            What drives the effectiveness of E-RECEIPTS.?
          </h1>
          <Row>
            <Col md={3} className="mb-4">
              <Card className="advantage-card" style={{ border: '2px solid  #007bff', borderRadius: '8px' }}>
                <Card.Body>
                  <Card.Title>Environmental Benefits</Card.Title>
                  <Card.Text>
                    <b>Reduced Waste:</b> E-receipts eliminate the need for
                    paper, saving trees and reducing landfill waste.
                    <br />
                    <b>Lower Carbon Footprint:</b> No physical production,
                    printing, or transportation reduces energy use and
                    emissions.
                  </Card.Text>
                </Card.Body>
              </Card>
            </Col>
            <Col md={3} className="mb-4">
              <Card className="advantage-card" style={{ border: '2px solid  #007bff', borderRadius: '8px' }}>
                <Card.Body>
                  <Card.Title>Cost Savings</Card.Title>
                  <Card.Text>
                    <b>No Printing Costs:</b> Businesses save money on printers,
                    paper, ink, and maintenance. <br />
                    <b>Streamlined Processes:</b> No need to handle paper or
                    dispose of waste reduces operational overhead.
                  </Card.Text>
                </Card.Body>
              </Card>
            </Col>
            <Col md={3} className="mb-4">
              <Card className="advantage-card" style={{ border: '2px solid  #007bff', borderRadius: '8px' }}>
                <Card.Body>
                  <Card.Title>Convenience for Customers</Card.Title>
                  <Card.Text>
                    <b>Searchability:</b> Customers can search in their app to
                    find receipts instead of sorting through paper. <br />
                    <b>Portable:</b> Accessible on devices like smartphones,
                    tablets, or computers anytime, anywhere.
                  </Card.Text>
                </Card.Body>
              </Card>
            </Col>
            <Col md={3} className="mb-4">
              <Card className="advantage-card" style={{ border: '2px solid  #007bff', borderRadius: '8px' }}>
                <Card.Body>
                  <Card.Title>Enhanced Business Insights</Card.Title>
                  <Card.Text>
                    <b>Analytics:</b> E-receipts provide valuable data for
                    analyzing purchasing habits and preferences. <br />
                    <b>Targeted Marketing:</b>Businesses can use e-receipts to
                    include promotional offers, loyalty program details
                  </Card.Text>
                </Card.Body>
              </Card>
            </Col>
          </Row>
          <Row>
            <Col md={3} className="mb-4">
              <Card className="advantage-card" style={{ border: '2px solid  #007bff', borderRadius: '8px' }}>
                <Card.Body>
                  <Card.Title>Better Customer Experience</Card.Title>
                  <Card.Text>
                    <b>Customization:</b> E-receipts can include logos, links,
                    and messages, making them more engaging. <br />
                    <b>Paper-Free Transactions:</b> Many customers appreciate
                    the eco-friendly and clutter-free alternative.
                  </Card.Text>
                </Card.Body>
              </Card>
            </Col>
            <Col md={3} className="mb-4">
              <Card className="advantage-card" style={{ border: '2px solid  #007bff', borderRadius: '8px' }}>
                <Card.Body>
                  <Card.Title>Durability and Longevity</Card.Title>
                  <Card.Text>
                    <b>No Fading:</b> Unlike thermal paper receipts, which fade
                    over time, e-receipts remain intact and legible.
                    <br />
                    <b>Backup Options:</b>
                    Digital records are easily backed up to cloud services,
                    ensuring no accidental loss.
                  </Card.Text>
                </Card.Body>
              </Card>
            </Col>
            <Col md={3} className="mb-4">
              <Card className="advantage-card" style={{ border: '2px solid  #007bff', borderRadius: '8px' }}>
                <Card.Body>
                  <Card.Title>Security and Integration</Card.Title>
                  <Card.Text>
                    <b>Fraud Prevention:</b> Digital receipts are harder to
                    forge than paper ones.
                    <br />
                    <b>Integration with Apps:</b>
                    E-receipts can be automatically synced with financial apps
                    or accounting software for better personal or business
                    record-keeping.
                  </Card.Text>
                </Card.Body>
              </Card>
            </Col>
            <Col md={3} className="mb-4">
              <Card className="advantage-card" style={{ border: '2px solid  #007bff', borderRadius: '8px' }}>
                <Card.Body>
                  <Card.Title>Compliance and Returns</Card.Title>
                  <Card.Text>
                    <b>Proof of Purchase:</b> E-receipts are widely accepted for
                    returns or warranty claims.
                    <br />
                    <b>Tax Compliance: </b>
                    Simplifies tax reporting by making expense tracking easier.
                  </Card.Text>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
      <div>
        <Container
          fluid
          className="hero-section1 align-items-center justify-content-center mt-5"
        >
          <div className="text-center">
            <span className="highlight-text">
              Environmental Impact of 1 Ton of Paper Receipts
            </span>
          </div>
          <br />
          <Row>
            <Col md={6} className="mb-4">
              <Card className="advantage-card" style={{ border: '2px solid  #007bff', borderRadius: '8px' }}>
                <Card.Body>
                  <Card.Title>Key Environmental Metrics</Card.Title>
                  <ListGroup variant="flush">
                    <ListGroup.Item>
                      🌳 Trees Cut: {impactData.treesCut} trees
                    </ListGroup.Item>
                    <ListGroup.Item>
                      🌍 CO2 Emissions: {impactData.co2Emissions} kg
                    </ListGroup.Item>
                    <ListGroup.Item>
                      💧 Water Usage: {impactData.waterUsage} liters
                    </ListGroup.Item>
                    <ListGroup.Item>
                      ⚡ Energy Used: {impactData.energyUsed} kWh
                    </ListGroup.Item>
                  </ListGroup>
                </Card.Body>
              </Card>
            </Col>
            <Col md={6} className="mb-4">
              <Card className="advantage-card" style={{ border: '2px solid  #007bff', borderRadius: '8px' }}>
                <Card.Body>
                  <Card.Title>Possible Actions to Reduce Impact</Card.Title>
                  <ListGroup variant="flush">
                    <ListGroup.Item>
                      📱 Switch to digital receipts.
                    </ListGroup.Item>
                    <ListGroup.Item>
                      🌿 Recycle paper receipts properly.
                    </ListGroup.Item>
                    <ListGroup.Item>
                      ♻️ Encourage businesses to reduce paper usage.
                    </ListGroup.Item>
                    <ListGroup.Item>
                      🔋 Support renewable energy initiatives.
                    </ListGroup.Item>
                  </ListGroup>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
    </div>
  );
}

export default About;
